import { useEffect, useState } from 'react';

import type { QRCode } from '@notify/shared';
import { QRStatus } from '@notify/shared';

import type { ShopWithDetails } from '../../services/admin.service';
import { adminService } from '../../services/admin.service';

// ─── Helpers ──────────────────────────────────────────────────────────────────

type FilterTab = 'all' | 'unlinked' | 'active' | 'retired';

function statusLabel(status: QRStatus): string {
  switch (status) {
    case QRStatus.UNLINKED:
      return 'Unlinked';
    case QRStatus.LINKED:
      return 'Active';
    case QRStatus.REVOKED:
      return 'Retired';
  }
}

function statusBadgeClass(status: QRStatus): string {
  switch (status) {
    case QRStatus.UNLINKED:
      return 'bg-gray-100 text-gray-600';
    case QRStatus.LINKED:
      return 'bg-green-100 text-green-700';
    case QRStatus.REVOKED:
      return 'bg-red-100 text-red-600';
  }
}

function matchesFilter(code: QRCode, filter: FilterTab): boolean {
  if (filter === 'all') return true;
  if (filter === 'unlinked') return code.status === QRStatus.UNLINKED;
  if (filter === 'active') return code.status === QRStatus.LINKED;
  if (filter === 'retired') return code.status === QRStatus.REVOKED;
  return true;
}

// ─── Generate modal ───────────────────────────────────────────────────────────

interface GenerateModalProps {
  onClose: () => void;
  onGenerated: (codes: QRCode[]) => void;
  onError: (msg: string) => void;
}

function GenerateModal({ onClose, onGenerated, onError }: GenerateModalProps) {
  const [count, setCount] = useState(1);
  const [loading, setLoading] = useState(false);

  async function handleGenerate() {
    setLoading(true);
    try {
      const codes = await adminService.generateQRCodes(count);
      onGenerated(codes);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to generate QR codes');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-base font-semibold text-[#0F172A]">Generate QR Codes</h2>
        <p className="mt-1 text-sm text-[#64748B]">How many QR codes would you like to generate?</p>
        <div className="mt-5">
          <label htmlFor="qr-count" className="block text-sm font-medium text-[#0F172A]">
            Count (1–50)
          </label>
          <input
            id="qr-count"
            type="number"
            min={1}
            max={50}
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(50, Number(e.target.value))))}
          />
        </div>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={loading}
            className="flex-1 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
          >
            {loading ? 'Generating…' : `Generate ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Link modal ───────────────────────────────────────────────────────────────

interface LinkModalProps {
  token: string;
  shops: ShopWithDetails[];
  onClose: () => void;
  onLinked: (code: QRCode) => void;
  onError: (msg: string) => void;
}

function LinkModal({ token, shops, onClose, onLinked, onError }: LinkModalProps) {
  const [selectedShopId, setSelectedShopId] = useState('');
  const [loading, setLoading] = useState(false);

  const unlinkedShops = shops.filter((s) => s.isActive);

  async function handleLink() {
    if (!selectedShopId) return;
    setLoading(true);
    try {
      const code = await adminService.linkQRCode(token, selectedShopId);
      onLinked(code);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to link QR code');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="text-base font-semibold text-[#0F172A]">Link QR Code to Shop</h2>
        <p className="mt-1 font-mono text-xs text-[#64748B] truncate">{token}</p>
        <div className="mt-5">
          <label htmlFor="shop-select" className="block text-sm font-medium text-[#0F172A]">
            Select Shop
          </label>
          <select
            id="shop-select"
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            value={selectedShopId}
            onChange={(e) => setSelectedShopId(e.target.value)}
          >
            <option value="">— Choose a shop —</option>
            {unlinkedShops.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} — {s.location}
              </option>
            ))}
          </select>
        </div>
        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-[#64748B] hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleLink()}
            disabled={loading || !selectedShopId}
            className="flex-1 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 disabled:opacity-60 transition-colors"
          >
            {loading ? 'Linking…' : 'Link'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── QR card ──────────────────────────────────────────────────────────────────

interface QRCardProps {
  code: QRCode;
  shopName: string | null;
  shops: ShopWithDetails[];
  onLink: (token: string) => void;
  onRetire: (codeId: string) => void;
  retiring: boolean;
}

function QRCard({ code, shopName, shops: _shops, onLink, onRetire, retiring }: QRCardProps) {
  return (
    <div className="flex flex-col rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* QR image */}
      <div className="flex items-center justify-center bg-[#F8FAFC] p-4">
        {code.qrImageUrl ? (
          <img
            src={code.qrImageUrl}
            alt={`QR code ${code.token}`}
            className="h-32 w-32 object-contain"
          />
        ) : (
          <div className="flex h-32 w-32 items-center justify-center rounded-lg bg-gray-100">
            <svg className="h-10 w-10 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <path d="M14 14h3v3M17 14v3M14 17h3" strokeLinecap="round" />
            </svg>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <p className="truncate font-mono text-xs text-[#64748B]" title={code.token}>
          {code.token}
        </p>

        <div className="flex items-center justify-between">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(code.status)}`}
          >
            {statusLabel(code.status)}
          </span>
          <span className="text-xs text-[#94A3B8]">
            {shopName ?? 'Unlinked'}
          </span>
        </div>

        {/* Actions */}
        {code.status === QRStatus.UNLINKED && (
          <button
            type="button"
            onClick={() => onLink(code.token)}
            className="mt-auto w-full rounded-lg border border-[#6366F1] px-3 py-1.5 text-xs font-medium text-[#6366F1] hover:bg-indigo-50 transition-colors"
          >
            Link to Shop
          </button>
        )}
        {code.status === QRStatus.LINKED && (
          <button
            type="button"
            disabled={retiring}
            onClick={() => onRetire(code.id)}
            className="mt-auto w-full rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-500 hover:bg-red-50 disabled:opacity-50 transition-colors"
          >
            {retiring ? 'Retiring…' : 'Retire'}
          </button>
        )}
        {code.status === QRStatus.REVOKED && (
          <div className="mt-auto rounded-lg bg-gray-50 px-3 py-1.5 text-center text-xs text-[#94A3B8]">
            Retired
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastItem {
  id: number;
  type: 'success' | 'error';
  message: string;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminQRPage() {
  const [qrCodes, setQRCodes] = useState<QRCode[]>([]);
  const [shops, setShops] = useState<ShopWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterTab>('all');
  const [generateOpen, setGenerateOpen] = useState(false);
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [retiring, setRetiring] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  function showToast(type: 'success' | 'error', message: string) {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }

  useEffect(() => {
    void (async () => {
      try {
        const [codes, shopList] = await Promise.all([
          adminService.getQRCodes(),
          adminService.getShops(),
        ]);
        setQRCodes(codes);
        setShops(shopList);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleRetire(codeId: string) {
    if (!window.confirm('Retire this QR code? It will no longer be usable.')) return;
    setRetiring(codeId);
    try {
      const updated = await adminService.retireQRCode(codeId);
      setQRCodes((prev) => prev.map((c) => (c.id === codeId ? updated : c)));
      showToast('success', 'QR code retired');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to retire QR code');
    } finally {
      setRetiring(null);
    }
  }

  function handleLinked(updated: QRCode) {
    setQRCodes((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    showToast('success', 'QR code linked successfully');
  }

  function handleGenerated(newCodes: QRCode[]) {
    setQRCodes((prev) => [...newCodes, ...prev]);
    showToast('success', `${newCodes.length} QR code${newCodes.length === 1 ? '' : 's'} generated`);
  }

  const shopMap = new Map(shops.map((s) => [s.id, s.name]));
  const filtered = qrCodes.filter((c) => matchesFilter(c, filter));

  const stats = {
    total: qrCodes.length,
    unlinked: qrCodes.filter((c) => c.status === QRStatus.UNLINKED).length,
    active: qrCodes.filter((c) => c.status === QRStatus.LINKED).length,
    retired: qrCodes.filter((c) => c.status === QRStatus.REVOKED).length,
  };

  const TABS: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: stats.total },
    { key: 'unlinked', label: 'Unlinked', count: stats.unlinked },
    { key: 'active', label: 'Active', count: stats.active },
    { key: 'retired', label: 'Retired', count: stats.retired },
  ];

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div /> {/* spacer — title is in the layout header */}
          <button
            onClick={() => setGenerateOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-[#6366F1] px-4 py-2 text-sm font-medium text-white hover:bg-indigo-600 transition-colors"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" strokeLinecap="round" />
              <line x1="5" y1="12" x2="19" y2="12" strokeLinecap="round" />
            </svg>
            Generate QR Codes
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-600">
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            { label: 'Total', value: stats.total, color: 'text-[#0F172A]' },
            { label: 'Unlinked', value: stats.unlinked, color: 'text-gray-500' },
            { label: 'Active', value: stats.active, color: 'text-green-600' },
            { label: 'Retired', value: stats.retired, color: 'text-red-500' },
          ].map(({ label, value, color }) => (
            <div
              key={label}
              className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
            >
              <p className="text-sm font-medium text-[#64748B]">{label}</p>
              <p className={`mt-2 text-3xl font-bold ${color}`}>{loading ? '—' : value}</p>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 rounded-lg bg-[#F1F5F9] p-1 w-fit">
          {TABS.map(({ key, label, count }) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-white text-[#0F172A] shadow-sm'
                  : 'text-[#64748B] hover:text-[#0F172A]'
              }`}
            >
              {label}
              <span className="ml-1.5 text-xs font-normal text-[#94A3B8]">{count}</span>
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div className="flex h-40 items-center justify-center bg-[#F8FAFC]">
                  <div className="h-28 w-28 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="space-y-2 p-4">
                  <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white py-16 text-center shadow-sm">
            <svg
              className="mb-4 h-12 w-12 text-gray-300"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <path d="M14 14h3v3M17 14v3M14 17h3" strokeLinecap="round" />
            </svg>
            <p className="text-sm font-medium text-[#0F172A]">No QR codes found</p>
            <p className="mt-1 text-xs text-[#64748B]">
              {filter === 'all' ? 'Generate your first QR codes above.' : `No ${filter} QR codes.`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {filtered.map((code) => (
              <QRCard
                key={code.id}
                code={code}
                shopName={code.linkedShopId ? (shopMap.get(code.linkedShopId) ?? null) : null}
                shops={shops}
                onLink={setLinkToken}
                onRetire={(id) => void handleRetire(id)}
                retiring={retiring === code.id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {generateOpen && (
        <GenerateModal
          onClose={() => setGenerateOpen(false)}
          onGenerated={handleGenerated}
          onError={(msg) => showToast('error', msg)}
        />
      )}
      {linkToken && (
        <LinkModal
          token={linkToken}
          shops={shops}
          onClose={() => setLinkToken(null)}
          onLinked={handleLinked}
          onError={(msg) => showToast('error', msg)}
        />
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
          {toasts.map((t) => (
            <div
              key={t.id}
              className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg ${
                t.type === 'success' ? 'bg-green-500' : 'bg-red-500'
              }`}
            >
              {t.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
